// =============================================================================
//  rank-offers.ts — ONE ranking truth for "best place to buy" and "best price".
//
//  PURE. No env, no I/O, no clock, no randomness. Imported by edge (`/go`),
//  server (page render, the engine API) and client bundles alike. The merchant
//  JSON is optional metadata (display names, a trust hint for known chains).
//  It is not a whitelist of who may appear.
//
//  DETERMINISTIC. The same array in always produces the same array out, down to
//  the order. Every comparison ends in a total-order tiebreak, because two
//  merchants quoting the identical price is not an edge case — it is Tuesday.
//
//  WHY READ-TIME AND NOT STORED: three independent writers land rows in
//  `product_offers` (the Amazon Creators refresh, the gateway's /find-offers
//  pipeline, find-product's discarded candidates). A stored "best price" flag is
//  wrong the moment the fourth row arrives, and nothing would ever recompute it.
//  0034 therefore has no is_best_* columns on purpose; this function is the
//  only place those labels exist.
//
//  SAVINGS IN ANOTHER COUNTRY ARE NOT SAVINGS. Everything here is scoped to one
//  region: a cheaper price in the wrong currency is not an offer this reader can
//  act on, and it is never allowed to win.
// =============================================================================

import OFFER_MERCHANTS from "../../generated/offer-merchants.json";
import type { Offer } from "../../types/product.type";
import { REGION_FOR_CODE, RegionCode } from "../../types/region.type";
import { getCurrencyForRegion } from "../currency";
import { normalizeMerchantDomain } from "./merchant-domain";

/** Host identity (lowercase, no scheme, no "www.", no trailing dot). Defined
 *  in merchant-domain.ts, the self-contained module the box copies verbatim,
 *  and re-exported here so every existing importer keeps its path. */
export { normalizeMerchantDomain };

// ─── The reviewed-merchant registry ──────────────────────────────────────────

export type OfferMerchantTier = "amazon" | "major-retailer" | "brand-store" | "other";

export interface OfferMerchant {
  name: string;
  tier: OfferMerchantTier;
  /** 1 = national flagship, 2 = solid national retailer, 3 = niche. */
  trustTier: number;
  /** RegionCodes this merchant is reviewed for. */
  regions: string[];
}

/** The frozen registry, generated from the gateway's per-country registry files
 *  by scripts/generate-offer-merchants.ts. Regenerate after any registry edit. */
export const OFFER_MERCHANTS_MAP: Readonly<Record<string, OfferMerchant>> =
  OFFER_MERCHANTS as unknown as Record<string, OfferMerchant>;

/** Registry keys, longest first — so 'shop.bestbuy.ca' resolves to the longest
 *  matching suffix rather than whichever key the object happened to list first.
 *  Computed once at module load; the registry is frozen data. */
const MERCHANT_KEYS_BY_LENGTH = Object.keys(OFFER_MERCHANTS_MAP).sort(
  (a, b) => b.length - a.length || (a < b ? -1 : 1),
);

/**
 * Catalogue entry for a merchant host, or null when we have no stored name /
 * trust hint. Suffix-aware: the file stores bare domains ('bestbuy.ca') but an
 * offer URL can live on a subdomain ('shop.bestbuy.ca'). Null is not a reason
 * to hide the row — rankOffers still shows it and scores it as fully trusted.
 */
export function lookupOfferMerchant(domain: string | null | undefined): OfferMerchant | null {
  if (!domain) return null;
  const host = normalizeMerchantDomain(domain);
  if (!host) return null;
  const exact = OFFER_MERCHANTS_MAP[host];
  if (exact) return exact;
  for (const key of MERCHANT_KEYS_BY_LENGTH) {
    if (host.endsWith(`.${key}`)) return OFFER_MERCHANTS_MAP[key] ?? null;
  }
  return null;
}

/** True when this merchant is reviewed FOR this market (not merely reviewed). */
export function merchantServesRegion(
  domain: string | null | undefined,
  regionCode: string,
): boolean {
  const merchant = lookupOfferMerchant(domain);
  return !!merchant && merchant.regions.includes(regionCode.toUpperCase());
}

// ─── Scoring constants ───────────────────────────────────────────────────────
//
// "Best place" is not "cheapest". It is where we would send a friend: a shop
// whose price we actually read, that has the thing in stock, and that is not
// gouging. The four weights below are that sentence in numbers, and they sum
// to 1 so the score is directly comparable across products and regions.
//
//   TRUST 0.4          — a hint when we already know the chain (Amazon vs a
//                        niche listing). An unknown shop that survived the
//                        offer pipeline scores the same as a flagship: we do
//                        not maintain a human-reviewed allowlist of who may
//                        sell. The AI (or the page extractor) already chose.
//   CONFIDENCE 0.3     — how sure we are the price is real. A number scraped
//                        from a search snippet is not a number read off the
//                        merchant's own page, and the ranking must feel that.
//   IN STOCK 0.2       — an out-of-stock "best place" is a dead end. It is not
//                        the top term because stock data is the least reliable
//                        field we collect.
//   NEAR BEST 0.1      — a nudge, not a lever: being within 10% of the cheapest
//                        offer breaks ties toward the shop that is also fair.
//                        Deliberately small, so bestPlace can and often will
//                        differ from bestPrice — that difference is the feature.
export const BEST_PLACE_WEIGHT_TRUST = 0.4;
export const BEST_PLACE_WEIGHT_CONFIDENCE = 0.3;
export const BEST_PLACE_WEIGHT_IN_STOCK = 0.2;
export const BEST_PLACE_WEIGHT_NEAR_BEST_PRICE = 0.1;

/** Within this fraction of the cheapest eligible price counts as "near best". */
export const NEAR_BEST_PRICE_TOLERANCE = 0.1;

/** Below this, a price is shown with a "~" and can never be labelled best
 *  price. 'creators-api' bypasses it: a first-party API response is not a
 *  guess, and holding it to a heuristic floor would demote the one number we
 *  actually trust. */
export const MIN_OFFER_CONFIDENCE = 0.5;

/** What a scraped offer is worth when the pipeline stored no number at all.
 *
 *  `confidence: null` is a NORMAL outcome of the Cursor /find-offers task — the
 *  agent found a real listing and did not score itself (cursor-tasks.ts). Two
 *  files used to disagree about what that null meant: the ranker read it as 0
 *  (below MIN_OFFER_CONFIDENCE, so the row could never carry a label) while the
 *  panel's `isApproximate` read it as "not a number, therefore not approximate"
 *  and printed the price as hard fact. The row was simultaneously too dubious to
 *  recommend and solid enough to quote. That contradiction is what handed every
 *  pick to the one row with an automatic 1.0 — the first-party Amazon feed —
 *  no matter what it cost. One resolver now answers for both files. */
export const DEFAULT_OFFER_CONFIDENCE = 0.6;

/** How far above the cheapest eligible price our pick may ever sit.
 *
 *  The weighted score below can only dock a dear merchant 0.1 (the near-best
 *  nudge), while `creators-api` provenance is worth up to 0.3 — so score alone
 *  let the pick drift arbitrarily far from the best price, and it did: a $316.06
 *  Amazon row was recommended over a $214.95 one, 47% dearer. Preference is a
 *  tiebreak among defensible options, never a licence to ignore the price. A
 *  candidate outside this band cannot be the pick at any trust tier. */
export const MAX_PICK_PREMIUM = 0.15;

/** Host labels that are never a brand, so a brand slug may not match them. */
const NON_BRAND_LABELS = new Set([
  "com", "net", "org", "co", "ca", "uk", "us", "eu", "de", "fr", "es", "it",
  "nl", "se", "pl", "jp", "br", "pt", "au", "shop", "store", "www", "shopping",
]);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** trustTier 1..3 → 1.0 / 0.667 / 0.333. Unknown shops score as flagship.
 *  A MARKETPLACE row (eBay, `merchantKind: 'marketplace'`) is tier 3 whatever
 *  the registry says: the listing is a third party's, not the host's. */
function trustScore(merchant: OfferMerchant | null, offer?: Offer): number {
  if (offer?.merchantKind === "marketplace") return 1 / 3;
  if (!merchant) return 1;
  const tier = Number(merchant.trustTier);
  if (!Number.isFinite(tier)) return 1;
  const clamped = Math.min(3, Math.max(1, tier));
  return (4 - clamped) / 3;
}

/**
 * The writers that set `merchant_kind` on purpose (migration 0045): the feed
 * matcher, which copies it from `merchant_programs`, and the brand-site
 * reader, which writes 'brand' only for a registered brand domain.
 *
 * Every other writer predates the column and never sets it, so its rows carry
 * the column DEFAULT 'retailer' (0045 added it NOT NULL DEFAULT 'retailer',
 * which stamped every existing row too). That value is not a classification:
 * on 2026-09-13, 26 live find-offers rows on a brand's own site (apple.com for
 * Apple, garmin.com for Garmin, stihl.ca for Stihl) read 'retailer'. The spec's
 * rule "the slug heuristic speaks only for rows written before 0045" is
 * therefore applied by writer, not by the field's presence.
 */
const MERCHANT_KIND_WRITERS: ReadonlySet<string> = new Set(["feed", "brand-site"]);

/**
 * True when a row's `merchantKind` is a fact rather than the column default:
 * 'brand' and 'marketplace' are only ever written deliberately, and
 * 'retailer' counts when a classifying writer wrote it. False for a row with
 * no kind at all (a payload from before 0045).
 */
export function hasClassifiedMerchantKind(offer: Pick<Offer, "merchantKind" | "source">): boolean {
  if (offer.merchantKind === undefined) return false;
  if (offer.merchantKind !== "retailer") return true;
  return MERCHANT_KIND_WRITERS.has(offer.source);
}

/** The fields the kind rules read. Structural rather than `Offer` so the
 *  panel's own row type (offers.ts, which also knows a 'search' kind) can
 *  share these functions instead of restating them. */
type OfferKindFields = {
  kind?: string;
  priceKind?: string;
  availability?: string;
};

/** A row that exists to be listed, never to be recommended: a brand
 *  information link (`kind: 'info'`), or a list / member / hidden price
 *  (spec section 6.5). Stored and shown, never labelled, never the pick. */
export function isLabelIneligibleKind(offer: OfferKindFields): boolean {
  if (offer.kind === "info") return true;
  const priceKind = offer.priceKind;
  return priceKind === "msrp" || priceKind === "member" || priceKind === "hidden";
}

/**
 * Where a row sits in the final order, before price is even looked at:
 *   0  a priced sale / regular / member row (the ones a reader can act on)
 *   1  an MSRP row (a list price, after every real price)
 *   2  a hidden price ("see price at ..."), before the dead ends
 *   3  out of stock
 *   4  an information link, last of all
 * Shared with the panel's own sort (offers.ts) so the two never disagree.
 */
export function offerPresentationTier(offer: OfferKindFields): number {
  if (offer.kind === "info") return 4;
  if (offer.availability === "out_of_stock") return 3;
  if (offer.priceKind === "hidden") return 2;
  if (offer.priceKind === "msrp") return 1;
  return 0;
}

/**
 * The confidence every surface must use — the ranker here AND the panel's
 * `isApproximate`. Exported for exactly that reason: see DEFAULT_OFFER_CONFIDENCE
 * for what the two of them used to disagree about.
 *
 *   a stored number  → that number, clamped to 0..1
 *   null / absent    → 1 for the first-party creators-api row (it came from
 *                      Amazon's own API, not a page we parsed), otherwise
 *                      DEFAULT_OFFER_CONFIDENCE — an unscored agent find is an
 *                      ordinary offer, not a worthless one.
 *
 * An EXPLICIT low number is still respected and still fails the floor. Only the
 * absence of a score defaults; a pipeline that says "0.2" means 0.2.
 *
 * Structural on purpose: the panel's own row type (offers.ts) knows a 'search'
 * kind no stored offer can have, and it resolves confidence through this same
 * function.
 */
export function resolveOfferConfidence(offer: Pick<Offer, "confidence" | "source">): number {
  const stored = offer.confidence;
  if (typeof stored === "number" && Number.isFinite(stored)) {
    return Math.min(1, Math.max(0, stored));
  }
  return offer.source === "creators-api" ? 1 : DEFAULT_OFFER_CONFIDENCE;
}

function confidenceOf(offer: Offer): number {
  return resolveOfferConfidence(offer);
}

/** The brand's own storefront, by registry tier or by name-to-host match.
 *
 *  The registry holds 38 major RETAILERS, so almost no brand store is in it —
 *  deuter.com and arcteryx.com both resolve to null. Matching the product's own
 *  brand against a host label is therefore the rule that actually fires: brand
 *  "Arc'teryx" → slug "arcteryx" → a label of `arcteryx.com` and of
 *  `shop.arcteryx.ca`. Punctuation and spacing are stripped from both sides. */
function brandSlugOf(brand: string | null | undefined): string | null {
  if (!brand) return null;
  const slug = brand.toLowerCase().replace(/[^a-z0-9]/g, "");
  return slug.length >= 3 ? slug : null;
}

export function isBrandStore(
  domain: string | null | undefined,
  brand: string | null | undefined,
): boolean {
  if (lookupOfferMerchant(domain)?.tier === "brand-store") return true;
  const slug = brandSlugOf(brand);
  if (!slug || !domain) return false;
  return normalizeMerchantDomain(domain)
    .split(".")
    .some((label) => label.length >= 3 && !NON_BRAND_LABELS.has(label) && label === slug);
}

function hasUsablePrice(offer: Offer): boolean {
  const value = offer.price?.value;
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isOutOfStock(offer: Offer): boolean {
  return offer.availability === "out_of_stock";
}

/** ISO 4217 for a market, or null when the region code is not one we serve. */
export function currencyForRegionCode(regionCode: string): string | null {
  const region = REGION_FOR_CODE[regionCode.toUpperCase() as RegionCode];
  return region ? getCurrencyForRegion(region) : null;
}

/**
 * The tiebreak chain, used identically for bestPrice, bestPlace and the final
 * sort so no two callers can disagree about which of two equals comes first:
 * higher confidence → the first-party Amazon row → merchant domain A-Z → url.
 * The last two make the order TOTAL, which is what makes this file testable.
 */
function compareTiebreak(a: Offer, b: Offer): number {
  const ca = confidenceOf(a);
  const cb = confidenceOf(b);
  if (ca !== cb) return cb - ca;

  const aFirstParty = a.source === "creators-api" ? 0 : 1;
  const bFirstParty = b.source === "creators-api" ? 0 : 1;
  if (aFirstParty !== bFirstParty) return aFirstParty - bFirstParty;

  const da = normalizeMerchantDomain(a.merchantDomain);
  const db = normalizeMerchantDomain(b.merchantDomain);
  if (da !== db) return da < db ? -1 : 1;

  const ua = a.url ?? "";
  const ub = b.url ?? "";
  return ua < ub ? -1 : ua > ub ? 1 : 0;
}

// ─── The ranker ──────────────────────────────────────────────────────────────

/**
 * Label and order one product's offers for ONE market.
 *
 * Returns a new array of new objects — the input is never mutated, and every
 * returned offer carries explicit `isBestPrice` / `isBestPlace` booleans so a
 * stale label that rode in from anywhere is overwritten rather than trusted.
 *
 * ELIGIBILITY — an offer may be LABELLED only when all of these hold:
 *   1. the price is known and positive;
 *   2. it is not out of stock;
 *   3. its currency is this region's currency (a foreign price is not a price);
 *   4. confidence ≥ MIN_OFFER_CONFIDENCE, unless it is the first-party
 *      creators-api row.
 *
 * A shop we have no catalogue entry for is still shown and may win a label.
 * An ineligible row (out of stock, unpriced, low confidence, wrong currency)
 * is KEPT and returned unlabelled. The panel still wants to say
 * "Currys: out of stock"; that is information, not a recommendation.
 *
 * Offers Engine v2 (docs/OFFERS_ENGINE_V2.md section 6.4, rules R14, R36):
 *   5. an information link (`kind: 'info'`) or a list, member or hidden price
 *      is never labelled and never the pick;
 *   6. a pre-order competes only when nothing in the market is in stock;
 *   7. a MARKETPLACE row (eBay) never wins bestPlace, and wins bestPrice only
 *      when it is itself in stock and the cheapest eligible offer.
 */
export function rankOffers(
  offers: Offer[],
  regionCode: string,
  options?: { brand?: string | null },
): Offer[] {
  if (!Array.isArray(offers) || offers.length === 0) return [];

  const regionCurrency = currencyForRegionCode(regionCode);
  const brand = options?.brand ?? null;

  // 1. Keep every row that is a real offer (host + URL). Catalogue lookup is
  //    only a name/trust hint. Entries carry their own index so winners below
  //    are identified by POSITION, not by object identity.
  const kept: { offer: Offer; merchant: OfferMerchant | null; index: number }[] = [];
  for (const offer of offers) {
    if (!offer?.merchantDomain || !offer.url) continue;
    kept.push({
      offer,
      merchant: lookupOfferMerchant(offer.merchantDomain),
      index: kept.length,
    });
  }
  if (kept.length === 0) return [];

  // 2. Eligibility for labels. Unknown shops compete on the same terms. An
  //    information link, a list price, a member price or a hidden price is
  //    listed but can carry no label and can never be the pick (spec 6.4.3).
  const eligibleAnyStock = kept.filter(({ offer }) => {
    if (isLabelIneligibleKind(offer)) return false;
    if (!hasUsablePrice(offer)) return false;
    if (isOutOfStock(offer)) return false;
    if (!regionCurrency || offer.price?.currency !== regionCurrency) return false;
    if (offer.source !== "creators-api" && confidenceOf(offer) < MIN_OFFER_CONFIDENCE) {
      return false;
    }
    return true;
  });
  //    A PRE-ORDER is a promise, not a shelf: it competes for a label only when
  //    nothing in the market is actually in stock (spec rule R36). A row with
  //    no stock signal at all ('unknown', absent) is neither, and stays as it
  //    always was.
  const anyInStock = eligibleAnyStock.some(({ offer }) => offer.availability === "in_stock");
  const eligible = anyInStock
    ? eligibleAnyStock.filter(({ offer }) => offer.availability !== "preorder")
    : eligibleAnyStock;

  // 3. Best price — the cheapest eligible offer, ties broken deterministically.
  //    A marketplace listing may carry "Best price" only when it says it is in
  //    stock (rule R14): a third party's listing with no stock signal is not a
  //    price a reader can count on, so it neither wins the label nor sets the
  //    floor the pick gate below is measured from.
  let bestPriceIndex = -1;
  let bestPriceOffer: Offer | null = null;
  for (const { offer, index } of eligible) {
    if (offer.merchantKind === "marketplace" && offer.availability !== "in_stock") continue;
    if (!bestPriceOffer) {
      bestPriceOffer = offer;
      bestPriceIndex = index;
      continue;
    }
    const delta = offer.price!.value - bestPriceOffer.price!.value;
    if (delta < 0 || (delta === 0 && compareTiebreak(offer, bestPriceOffer) < 0)) {
      bestPriceOffer = offer;
      bestPriceIndex = index;
    }
  }

  // 4. Best place — a PREFERENCE LADDER, gated on price.
  //
  //    rung 1  Amazon        — the market we know best and can price-verify
  //                            first-party.
  //    rung 2  the brand's own store — nobody knows the product better, and a
  //                            brand storefront cannot be a grey-market reseller.
  //    rung 3  the weighted score below, for everyone else.
  //
  //    THE GATE COMES FIRST, and it applies to every rung. A merchant more than
  //    MAX_PICK_PREMIUM above the cheapest eligible price is not a candidate at
  //    all — not at rung 1, not ever. Preference decides between defensible
  //    options; it does not make an indefensible price defensible. Without this,
  //    "prefer Amazon" reads as "recommend Amazon at any price", which is the
  //    $316-over-$214 failure it replaced.
  const lowest = bestPriceOffer?.price?.value ?? null;
  const ceiling = lowest === null ? null : lowest * (1 + MAX_PICK_PREMIUM);
  const affordable =
    ceiling === null ? [] : eligible.filter(({ offer }) => offer.price!.value <= ceiling);

  /** Cheapest of a rung, ties broken by the shared chain. The cheapest eligible
   *  offer is always inside the gate (it is the floor), so a non-empty
   *  `eligible` always yields a pick — the panel never loses its CTA. */
  const cheapestOf = (group: typeof eligible) =>
    group.reduce<(typeof eligible)[number] | null>((best, entry) => {
      if (!best) return entry;
      const delta = entry.offer.price!.value - best.offer.price!.value;
      if (delta < 0 || (delta === 0 && compareTiebreak(entry.offer, best.offer) < 0)) return entry;
      return best;
    }, null);

  //    A MARKETPLACE row (eBay) never enters rung 1 or 2: the host is not the
  //    seller, so "the market we know best" and "the brand's own store" are
  //    claims it cannot make. Nor does it enter rung 3 (rule R14, below).
  //
  //    RUNG 2 IS A FACT, NOT A GUESS, once a writer has classified the row
  //    (migration 0045): 'brand' means the merchant host equals the product
  //    brand's registered site (product_brands, 0046), and a classified
  //    'retailer' never enters rung 2 however much its host label resembles
  //    the brand. The name-to-host heuristic `isBrandStore` still speaks for
  //    the rows no writer classified: a payload with no kind, and a legacy
  //    writer's row holding the column default (hasClassifiedMerchantKind).
  const amazonRung = affordable.filter(
    ({ offer, merchant }) => merchant?.tier === "amazon" && offer.merchantKind !== "marketplace",
  );
  const brandRung = affordable.filter(({ offer, merchant }) => {
    if (offer.merchantKind === "brand") return true;
    if (hasClassifiedMerchantKind(offer)) return false;
    return merchant?.tier === "brand-store" || isBrandStore(offer.merchantDomain, brand);
  });

  let bestPlaceIndex = -1;
  let bestPlaceOffer: Offer | null = null;

  const preferred = cheapestOf(amazonRung) ?? cheapestOf(brandRung);
  if (preferred) {
    bestPlaceOffer = preferred.offer;
    bestPlaceIndex = preferred.index;
  } else {
    // rung 3 — the weighted score, unchanged, over gate-passing candidates only.
    //
    // A marketplace listing is never "where we would send a friend" (rule
    // R14): the host is not the seller. It is excluded outright rather than
    // merely down-weighted, so no confidence or stock score can lift it past a
    // retailer. When it is the only row inside the gate nothing here wins;
    // the panel's pick then falls back through pickPrimaryOffer (offers.ts),
    // which never leaves the CTA empty. Its trust term stays tier 3 wherever
    // a score is computed for it (trustScore).
    const rung3 = affordable.filter(({ offer }) => offer.merchantKind !== "marketplace");
    let bestPlaceScore = -1;
    for (const { offer, merchant, index } of rung3) {
      const nearBest =
        lowest !== null && offer.price!.value <= lowest * (1 + NEAR_BEST_PRICE_TOLERANCE) ? 1 : 0;
      const score =
        BEST_PLACE_WEIGHT_TRUST * trustScore(merchant, offer) +
        BEST_PLACE_WEIGHT_CONFIDENCE * confidenceOf(offer) +
        BEST_PLACE_WEIGHT_IN_STOCK * (offer.availability === "in_stock" ? 1 : 0) +
        BEST_PLACE_WEIGHT_NEAR_BEST_PRICE * nearBest;
      if (
        !bestPlaceOffer ||
        score > bestPlaceScore ||
        (score === bestPlaceScore && compareTiebreak(offer, bestPlaceOffer) < 0)
      ) {
        bestPlaceOffer = offer;
        bestPlaceScore = score;
        bestPlaceIndex = index;
      }
    }
  }

  // 5. Label copies, then order: our pick first, then the presentation tier
  //    (real prices, list prices, hidden prices, dead ends, information links
  //    last), cheap before dear, and the tiebreak chain to make the order
  //    total.
  const labelled = kept.map(({ offer, index }) => ({
    ...offer,
    isBestPrice: index === bestPriceIndex,
    isBestPlace: index === bestPlaceIndex,
  }));

  return labelled.sort((a, b) => {
    if (a.isBestPlace !== b.isBestPlace) return a.isBestPlace ? -1 : 1;

    const tierDelta = offerPresentationTier(a) - offerPresentationTier(b);
    if (tierDelta !== 0) return tierDelta;

    // Foreign-currency rows sink below every local one BEFORE prices are
    // compared: sorting $50 USD ahead of $100 CAD on a Canadian page would make
    // a row that is not even purchasable here look like the cheapest one.
    const foreign = (o: Offer) =>
      regionCurrency && o.price?.currency && o.price.currency !== regionCurrency ? 1 : 0;
    const aForeign = foreign(a);
    const bForeign = foreign(b);
    if (aForeign !== bForeign) return aForeign - bForeign;

    const aPriced = hasUsablePrice(a) ? 0 : 1;
    const bPriced = hasUsablePrice(b) ? 0 : 1;
    if (aPriced !== bPriced) return aPriced - bPriced;
    if (aPriced === 0 && a.price!.value !== b.price!.value) {
      return a.price!.value - b.price!.value;
    }

    return compareTiebreak(a, b);
  });
}
